import { useState, useEffect } from 'react';

const CATEGORIES = [
  'Personal Details',
  'Contact Information',
  'KYC Documents',
  'Financial Details',
  'Professional Details',
  'Family Details',
  'Referral',
];

const FIELD_TYPES = [
  'text', 'number', 'date', 'dropdown', 'radio',
  'checkbox', 'file_upload', 'phone', 'email', 'textarea',
];

function makeUid() {
  return 'field_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
}

export default function ClientProfileEditor({ fields, stages, onSave, saving }) {
  const [localFields, setLocalFields] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);

  useEffect(() => {
    setLocalFields(fields || []);
  }, [fields]);

  const categoryFields = localFields
    .filter(f => f.category === selectedCategory)
    .sort((a, b) => a.order - b.order);

  function handleChange(uid, key, value) {
    setLocalFields(prev => prev.map(f => f.uid === uid ? { ...f, [key]: value } : f));
  }

  function handleAdd() {
    const existing = localFields.filter(f => f.category === selectedCategory);
    const newField = {
      uid: makeUid(),
      label: 'New Field',
      field_type: 'text',
      category: selectedCategory,
      required: false,
      hint: '',
      options: [],
      stage_uid: '',
      order: existing.length + 1,
    };
    setLocalFields(prev => [...prev, newField]);
  }

  function handleDelete(uid) {
    if (!window.confirm('Delete this field?')) return;
    setLocalFields(prev => prev.filter(f => f.uid !== uid));
  }

  function handleSave() {
    onSave(localFields);
  }

  return (
    <div>
      <div className="pm__cat-tabs">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`pm__cat-tab ${selectedCategory === cat ? 'pm__cat-tab--active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat} ({localFields.filter(f => f.category === cat).length})
          </button>
        ))}
      </div>

      <div className="pm__toolbar">
        <span className="pm__section-title">{selectedCategory}</span>
        <button className="btn btn--outline-primary" onClick={handleAdd}>+ Add Field</button>
      </div>

      <div className="pm__field-list">
        {categoryFields.length === 0 && (
          <div className="pm__empty" style={{ marginTop: 'var(--space-lg)' }}>
            No fields in this category. Click + Add Field to create one.
          </div>
        )}
        {categoryFields.map(field => (
          <div key={field.uid} className="pm__field-card">
            <div className="pm__field-card-top">
              <input
                className="pm__input pm__input--label"
                value={field.label}
                onChange={e => handleChange(field.uid, 'label', e.target.value)}
                placeholder="Field label"
              />
              <select
                className="pm__select pm__select--type"
                value={field.field_type}
                onChange={e => handleChange(field.uid, 'field_type', e.target.value)}
              >
                {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <label className="pm__required-toggle">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={e => handleChange(field.uid, 'required', e.target.checked)}
                />
                Required
              </label>
              <select
                className="pm__select"
                style={{ minWidth: '130px' }}
                value={field.stage_uid || ''}
                onChange={e => handleChange(field.uid, 'stage_uid', e.target.value)}
              >
                <option value="">No stage</option>
                {(stages || []).map(s => (
                  <option key={s.uid} value={s.uid}>{s.icon} {s.name}</option>
                ))}
              </select>
              <button
                className="pm__del-btn"
                onClick={() => handleDelete(field.uid)}
                title="Delete field"
              >
                &#10005;
              </button>
            </div>
            <div className="pm__field-card-hint">
              <input
                className="pm__input pm__input--hint"
                placeholder="Hint text (e.g. As per PAN card)"
                value={field.hint || ''}
                onChange={e => handleChange(field.uid, 'hint', e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="pm__editor-footer">
        <button
          className={`btn ${saving ? 'btn--disabled' : 'btn--primary'}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
